'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { User, UserStatus } from '@bliks-io/contracts';
import { useOrgMembers, type UseOrgMembersOptions } from '@/api/queries/useOrgMembers';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  type ListboxItem,
  getUserInitials,
  getUserFullName,
  getUserAvatarUrl
} from '@bliks-io/ui';
import { TransferListbox, type TransferListboxProps } from './TransferListbox';

export interface UserListboxItem extends ListboxItem {
  id: string;
  /**
   * The full source user — kept on the item so `renderItem` overrides and
   * `availableFilter` predicates can reach fields beyond the default
   * avatar + name row (e.g. mainRole, department, deletedAt).
   */
  user: User;
}

export interface UserTransferListboxProps extends Omit<
  TransferListboxProps<UserListboxItem>,
  'items' | 'renderItem' | 'getItemId' | 'searchFilter'
> {
  /**
   * Optional filter to limit which users are shown as available
   * For example, filter by department
   */
  filterUsers?: (user: User) => boolean;

  /**
   * Custom left column label
   * @default "Available Users"
   */
  leftLabel?: string;

  /**
   * Custom right column label
   * @default "Selected Users"
   */
  rightLabel?: string;

  /**
   * Custom search placeholder
   * @default "Search users..."
   */
  searchPlaceholder?: string;

  /**
   * Query options for server-side filtering of users (API level).
   * Defaults to { status: ['inactive', 'pending', 'active'] } to show all assignable users.
   * This allows assigning inactive/pending users for preparation before activation.
   *
   * @default { status: ['inactive', 'pending', 'active'] }
   * @example queryOptions={{ departmentId: 'xyz' }} - Assignable users from specific department
   * @example queryOptions={{ status: ['active'] }} - Only active users (overrides default)
   * @example queryOptions={{ status: undefined }} - All users regardless of status (includes suspended)
   */
  queryOptions?: UseOrgMembersOptions;

  /**
   * Override the default avatar + name row, e.g. with a richer UserCard.
   * Items carry the full `user`, so overrides can render any user field.
   */
  renderItem?: (item: UserListboxItem, selected: boolean) => React.ReactNode;
}

/**
 * Specialized UserTransferListbox component with data fetching
 * Displays users with avatars and names
 * For use without React Hook Form. For RHF integration, use FormUserTransferListbox instead.
 *
 * By default, shows inactive, pending, and active users (suitable for assignments where preparation
 * before activation is needed). Suspended users are excluded by default.
 * For admin user management pages, set queryOptions={{ status: undefined }} to show all users.
 */
export const UserTransferListbox = React.forwardRef<HTMLDivElement, UserTransferListboxProps>(
  (
    {
      value,
      onChange,
      filterUsers,
      leftLabel,
      rightLabel,
      searchPlaceholder,
      disabled = false,
      label,
      description,
      error,
      required = false,
      id,
      name,
      orientation = 'vertical',
      fieldClassName,
      listboxClassName,
      queryOptions,
      availableFilterSlot,
      availableFilter,
      renderItem,
      lockedIds,
      lockedHint
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Merge default status: ['inactive', 'pending', 'active'] with provided queryOptions
    const options = { status: ['inactive', 'pending', 'active'] as UserStatus[], ...queryOptions };
    const { data: users, isLoading, isError } = useOrgMembers(options);

    // Convert users to listbox items
    const userItems = React.useMemo<UserListboxItem[]>(() => {
      if (!users) return [];

      let filteredUsers = users;
      if (filterUsers) {
        filteredUsers = users.filter(filterUsers);
      }

      return filteredUsers.map((user) => ({ id: user._id, user }));
    }, [users, filterUsers]);

    // Determine disabled state
    const isDisabled = disabled || isLoading || isError || !users || users.length === 0;

    // Determine error message
    let errorMessage = error;
    if (isLoading) {
      errorMessage = undefined; // Don't show error while loading
    } else if (isError || !users) {
      errorMessage = t('userTransferListbox.error', { defaultValue: 'Failed to load users' });
    } else if (users.length === 0) {
      errorMessage = t('userTransferListbox.noUsers', { defaultValue: 'No users available' });
    }

    // Render user item
    const renderUserItem = React.useCallback((item: UserListboxItem) => {
      const fullName = getUserFullName(item.user.firstName, item.user.lastName);
      const initials = getUserInitials(item.user.firstName, item.user.lastName);

      return (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={getUserAvatarUrl(item.user, 'thumbnail')} alt={fullName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium">{fullName}</span>
        </div>
      );
    }, []);

    // Search filter for users
    const searchFilterUsers = React.useCallback((item: UserListboxItem, query: string) => {
      const searchStr =
        `${item.user.firstName} ${item.user.lastName} ${item.user.email}`.toLowerCase();
      return searchStr.includes(query.toLowerCase());
    }, []);

    return (
      <div ref={ref}>
        <TransferListbox
          id={id}
          name={name}
          label={label}
          description={description}
          error={errorMessage}
          required={required}
          orientation={orientation}
          fieldClassName={fieldClassName}
          listboxClassName={listboxClassName}
          items={userItems}
          value={value}
          onChange={onChange}
          leftLabel={
            leftLabel ||
            t('userTransferListbox.availableUsers', { defaultValue: 'Available Users' })
          }
          rightLabel={
            rightLabel || t('userTransferListbox.selectedUsers', { defaultValue: 'Selected Users' })
          }
          searchPlaceholder={
            searchPlaceholder ||
            t('userTransferListbox.searchUsers', { defaultValue: 'Search users...' })
          }
          renderItem={renderItem ?? renderUserItem}
          getItemId={(item) => item.id}
          searchFilter={searchFilterUsers}
          disabled={isDisabled}
          availableFilterSlot={availableFilterSlot}
          availableFilter={availableFilter}
          lockedIds={lockedIds}
          lockedHint={lockedHint}
        />
      </div>
    );
  }
);

UserTransferListbox.displayName = 'UserTransferListbox';
